// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import type { StateType, DispatchType } from "../../../global/types/redux";
import QuoteList from "../../../account/components/quotes/QuoteList";
import { buildQuoteItemJson } from "../../../account/components/quotes/QuoteCompileFunctions";
import { fetchPriceSheet } from "../../../account/actions/quoteActions";
import {
  fetchAdminQuoteList,
  fetchChildrenList,
  fetchSheetCompanies,
  fetchProductSheetList
} from "../../actions/quoteAdminActions";
import {
  StyledSelect, GeneralModalForm, LoadingProgress, SnackbarMessage, SingleSwitch, NoItem
} from "imagus-components";
import ChipInput from "../../../global/components/ChipInput";
import InfiniteCalendarSelect from "../../../global/components/InfiniteCalendarSelect";
import { companyDialogMessages } from "../../../account/components/companies/CompileCompanytoJson";
import { ErrosDisplay } from "../../../account/containers/ErrosDisplay";
import type { ErrorsType } from "../../../account/types/UserFormType";
import { propComparator } from "../../../global/functions/StringOperations";
import PriceSheets from "./PriceSheets";
import PriceTiers from "./PriceTiers";

import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import LinkIcon from "@material-ui/icons/Link";

type AdminQuotesStateType = {
  isFilterOpen: boolean,
  order: "createdDate" | "projectName" | "companyName",
  suquence: "asc" | "desc",
  companies: Array<string>,
  start_date: Date | null,
  end_date: Date | null,
  licenceType: "ALL" | "LONGTERM" | "SHORTTERM",
  resellerType: "ALL" | "1" | "-1",
  tierType: string,
  priceType: string,
  messageId: number,
  errors: ErrorsType,
  snackOpen: boolean,
  modalOpen: boolean,
  tierModalOpen: boolean
};

class AdminQuotes extends Component<*, AdminQuotesStateType> {
  constructor(props) {
    super(props);
    this.state = {
      isFilterOpen: true,
      order: "createdDate",
      suquence: "asc",
      companies: [],
      start_date: null,
      end_date: null,
      licenceType: "ALL",
      resellerType: "ALL",
      tierType: "ALL",
      priceType: "ALL",
      messageId: 0,
      errors: {},
      snackOpen: false,
      modalOpen: false,
      tierModalOpen: false
    };
  }

  componentDidMount() {
    if (this.props.quotes) {
      this.fetchCompanies();
    } else {
      this.props.actions.fetchAdminQuoteList(this.fetchCompanies);
    }
    this.props.actions.fetchPriceSheet();
    this.props.actions.fetchSheetCompanies();
    if (this.props.productSheets === null) {
      this.props.actions.fetchProductSheetList();
    }
  }

  fetchCompanies = (): * => {
    const quotes = this.props.quotes && [...this.props.quotes];
    const companies = this.props.companies ? { ...this.props.companies } : {};
    quotes.forEach(
      (quote: *, i: number): * => {
        let companyId = quote.company.id;
        if (!(companyId in companies)) {
          this.props.actions.fetchChildrenList(companyId);
        }
      }
    );
  };

  switchModalStatus = (modalName: string): * => (status: boolean): * => {
    this.setState({ [modalName]: status });
  };

  openSnackbar = (messageId: number): * => {
    this.setState(
      { messageId: messageId },
      this.switchModalStatus("snackOpen")(true)
    );
  };

  setActionError = (errors: ErrorsType): * => {
    this.setState({
      errors: errors
    });
  };

  handleTypeChange = (e: SyntheticInputEvent<*>): * => {
    const name = e.target.name;
    const value = e.target.value;
    this.setState({ [name]: value });
  };

  syncChange = (name: string, value: Array<*>): * => {
    this.setState({ [name]: value });
  };

  syncDateChange = (name: string, date: Date): * => {
    this.setState({ [name]: date });
  };

  handleSwitchChange = (event: Event): * => {
    const name = event.target.name;
    const value = event.target.checked;
    this.setState({
      [name]: value
    });
  };

  render(): * {
    const quotes = this.props.quotes && [...this.props.quotes];
    let quoteNode = null;
    let companyList = [];
    if (quotes) {
      if (quotes.length === 0) {
        quoteNode = <NoItem content="No Quote has created yet." />;
      } else {
        if (this.state.order === "createdDate") {
          quotes.sort(
            propComparator("auditCreationDate", false, null, null, true)
          );
        } else if (this.state.order === "projectName") {
          quotes.sort(propComparator("quotedCompanyName", true));
        } else if (this.state.order === "companyName") {
          quotes.sort(propComparator("company", true, "name"));
        }

        if (this.state.suquence === "desc") {
          quotes.reverse();
        }

        let quoteList = [];
        for (let i = 0; i < quotes.length; i++) {
          let company = quotes[i].company;
          let index = companyList.findIndex(x => x.value === company.id);
          if (index < 0) {
            companyList.push({ value: company.id, label: company.name });
          }
          quoteList.push(buildQuoteItemJson(quotes[i]));
        }
        companyList.sort(propComparator("label", true));

        quoteNode = (
          <QuoteList
            quotes={quoteList}
            companyFilter={this.state.companies}
            dateStartFilter={this.state.start_date}
            dateEndFilter={this.state.end_date}
            licenceFilter={this.state.licenceType}
            resellerFilter={this.state.resellerType}
            tierFilter={this.state.tierType}
            priceFilter={this.state.priceType}
            isAdmin
            openSnackbar={this.openSnackbar}
            setActionError={this.setActionError}
            canEdit
          />
        );
      }
    }

    const OrderList = [
      { value: "createdDate", label: "Quote Created Date" },
      { value: "projectName", label: "For Company Name" },
      { value: "companyName", label: "Create Company Name" }
    ];
    const SequenceList = [
      { value: "asc", label: "Ascending" },
      { value: "desc", label: "Descending" }
    ];
    const LicenceTypes = [
      { value: "ALL", label: "ALL" },
      { value: "LONGTERM", label: "Long Term Licence" },
      { value: "SHORTTERM", label: "Short Term Licence" }
    ];
    const AuthorityTypes = [
      { value: "ALL", label: "ALL" },
      { value: "1", label: "Authorised Reseller" },
      { value: "-1", label: "Standard Reseller" }
    ];

    let TierList = [{ value: "ALL", label: "ALL" }];
    this.props.priceSheet &&
      this.props.priceSheet.tiers.map(
        (tier: *): * => {
          TierList.push({ value: tier.id, label: tier.name });
        }
      );

    let PriceList = [
      { value: "ALL", label: "ALL" },
      { value: "BASE", label: "Base Price" }
    ];
    this.props.productSheets &&
      this.props.productSheets.map(
        (productSheet: *): * => {
          PriceList.push({
            value: productSheet.id,
            label: productSheet.description
          });
        }
      );

    if (!quoteNode) {
      return (
        <div className="app-panel">
          <LoadingProgress />
        </div>
      );
    } else {
      return (
        <div className="app-panel">
          <div className="top-banner">
            <ErrosDisplay errors={this.state.errors} form="admin quote" />
          </div>
          <SnackbarMessage
            open={this.state.snackOpen}
            handleModalStatus={this.switchModalStatus("snackOpen")}
            message={companyDialogMessages[this.state.messageId]}
          />
          <div className="app-panel-content">
            <Paper elevation={4} style={{ padding: "16px " }}>
              <SingleSwitch
                value={this.state.isFilterOpen}
                name="isFilterOpen"
                isRequired={false}
                label={["Show Criteria", "Hide Criteria"]}
                handleChange={this.handleSwitchChange}
              />
              <Button
                type="button"
                variant="contained"
                color="primary"
                className="dark-btn"
                style={{ float: "right" }}
                onClick={(): Function =>
                  this.switchModalStatus("tierModalOpen")(true)
                }
              >
                <LinkIcon className="light-icon right-margin-5" />
                Tiers
              </Button>
              <Button
                type="button"
                variant="contained"
                color="primary"
                className="dark-btn right-margin-10"
                style={{ float: "right" }}
                onClick={(): Function =>
                  this.switchModalStatus("modalOpen")(true)
                }
              >
                <AttachMoneyIcon className="light-icon right-margin-5" />
                Price Sheets
              </Button>
              <GeneralModalForm
                title="Tiers"
                open={this.state.tierModalOpen}
                handleModalStatus={this.switchModalStatus("tierModalOpen")}
                maxMd
                form={
                  <PriceTiers
                    onClose={(): * =>
                      this.switchModalStatus("tierModalOpen")(false)
                    }
                  />
                }
              />
              <GeneralModalForm
                title="Quote Price Sheets"
                open={this.state.modalOpen}
                handleModalStatus={this.switchModalStatus("modalOpen")}
                fullScreen
                form={
                  <PriceSheets
                    onClose={(): * =>
                      this.switchModalStatus("modalOpen")(false)
                    }
                  />
                }
              />
              {this.state.isFilterOpen && (
                <Grid container spacing={24}>
                  <Grid item xs={12} md={6}>
                    <StyledSelect
                      value={this.state.order}
                      onChange={this.handleTypeChange}
                      name="order"
                      label="Order By"
                      isRequired={true}
                      menus={OrderList}
                      isStyled
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <StyledSelect
                      value={this.state.suquence}
                      onChange={this.handleTypeChange}
                      name="suquence"
                      label="Ascend | Descend"
                      isRequired={true}
                      menus={SequenceList}
                      isStyled
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <InfiniteCalendarSelect
                      isRequired={false}
                      label="Select Start Date"
                      name="start_date"
                      value={this.state.start_date}
                      syncDateChange={this.syncDateChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <InfiniteCalendarSelect
                      isRequired={false}
                      label="Select End Date"
                      name="end_date"
                      value={this.state.end_date}
                      syncDateChange={this.syncDateChange}
                      isEndDate
                    />
                  </Grid>
                  <Grid item xs={12} md={6} className="top-margin-10">
                    <ChipInput
                      name="companies"
                      label="Select Companies"
                      isMultiple
                      placeholder="All Companies"
                      tags={companyList}
                      value={this.state.companies}
                      syncChange={this.syncChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <StyledSelect
                      value={this.state.licenceType}
                      onChange={this.handleTypeChange}
                      name="licenceType"
                      label="Licence Type"
                      isRequired={true}
                      menus={LicenceTypes}
                      isStyled
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <StyledSelect
                      value={this.state.resellerType}
                      onChange={this.handleTypeChange}
                      name="resellerType"
                      label="Reseller Type"
                      isRequired={true}
                      menus={AuthorityTypes}
                      isStyled
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <StyledSelect
                      value={this.state.tierType}
                      onChange={this.handleTypeChange}
                      name="tierType"
                      label="Tier Type"
                      isRequired={true}
                      menus={TierList}
                      isStyled
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <StyledSelect
                      value={this.state.priceType}
                      onChange={this.handleTypeChange}
                      name="priceType"
                      label="Price Type"
                      isRequired={true}
                      menus={PriceList}
                      isStyled
                    />
                  </Grid>
                </Grid>
              )}
            </Paper>
          </div>
          {quoteNode}
        </div>
      );
    }
  }
}

const mapStateToProps = (state: StateType): * => ({
  user: state.v3account.user,
  quotes: state.v3admin.quote.quotes,
  companies: state.v3admin.quote.companies,
  priceSheet: state.v3account.quote.priceSheet,
  productSheets: state.v3admin.quote.productSheets
});

const mapDispatchToProps = (dispatch: DispatchType): DispatchType => ({
  actions: bindActionCreators(
    {
      fetchAdminQuoteList,
      fetchPriceSheet,
      fetchChildrenList,
      fetchSheetCompanies,
      fetchProductSheetList
    },
    dispatch
  )
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AdminQuotes);
